import axiosInstance from "./axiosInstance";
import axios from "axios";

interface PresignedUploadResponse {
  upload_url: string;
  s3_key: string;
}

/**
 * Upload a file to S3 using presigned URLs.
 * This approach bypasses CloudFront and supports files up to 5GB.
 *
 * @param file - The file to upload
 * @param path - The S3 path (without tenant prefix, it will be added by backend)
 * @returns Promise that resolves when upload is complete
 */
export async function uploadFileToS3(file: File, path: string): Promise<void> {
  // Step 1: Get presigned URL from backend
  const presignedResponse = await axiosInstance.post<PresignedUploadResponse>(
    "/api/s3/presigned-upload-url",
    {
      path: path,
      content_type: file.type || "application/octet-stream",
    }
  );

  const { upload_url } = presignedResponse.data;

  // Step 2: Upload file directly to S3 using presigned URL
  // Note: We use vanilla axios (not axiosInstance) to avoid adding auth headers
  await axios.put(upload_url, file, {
    headers: {
      "Content-Type": file.type || "application/octet-stream",
    },
  });
}

/**
 * Legacy upload method using the /api/s3/upload endpoint.
 * Limited to ~1MB due to CloudFront body size restrictions.
 * Use uploadFileToS3 instead for larger files.
 *
 * @param file - The file to upload
 * @param path - The S3 path (without tenant prefix)
 * @returns Promise that resolves when upload is complete
 * @deprecated Use uploadFileToS3 instead
 */
export async function uploadFileLegacy(file: File, path: string): Promise<void> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("path", path);
  await axiosInstance.post("/api/s3/upload", formData);
}
